/*
 * The partner's referral link: client onboarding with ?ref=<code>. A client
 * who onboards and pays through it is attributed to this partner, and their
 * first subscription payment earns one "add user" credit.
 */

import { useState } from "react";
import { useMe } from "../auth/AuthContext";
import { AddUserButton } from "./AddUserSheet";
import { copyText } from "./bits";
import { plural } from "./format";
import { Check, Copy, UserPlus } from "./icons";

export default function ReferralLinkCard({ onAddedUser }: { onAddedUser?: (name: string) => void }) {
  const me = useMe();
  const [copied, setCopied] = useState(false);
  const credits = me.user_credits;
  const message = `Start your Wealthoak account through my referral link: ${me.referral_link}`;
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function copy() {
    await copyText(me.referral_link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  async function share() {
    try {
      await navigator.share({ title: "Wealthoak", text: message, url: me.referral_link });
    } catch {
      /* dismissed */
    }
  }

  return (
    <section className="card card--pad ov-link" aria-labelledby="ov-link-title">
      <div className="ov-link__main">
        <p className="eyebrow">Your referral link</p>
        <h2 id="ov-link-title">Share it with people you refer</h2>
        <p className="ov-link__sub">
          When someone onboards and pays for a Wealthoak plan through this link, they&apos;re added to your clients and
          you get <strong>+1 add-user credit</strong>.
        </p>
        <div className="ov-link__row">
          <input
            className="ov-link__url"
            value={me.referral_link}
            readOnly
            aria-label="Referral link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button className="button button--primary" onClick={() => void copy()} aria-live="polite">
            {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy link"}
          </button>
          <a
            className="button button--secondary"
            href={`https://wa.me/?text=${encodeURIComponent(message)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp
          </a>
          {canShare && (
            <button className="button button--secondary" onClick={() => void share()}>
              Share…
            </button>
          )}
        </div>
      </div>
      <div className="ov-link__credits" aria-label="Add-user credits">
        <UserPlus size={18} />
        <strong className="num">{credits.available}</strong>
        <span>add-user {credits.available === 1 ? "credit" : "credits"} available</span>
        <small>
          {credits.earned
            ? `${plural(credits.earned, "client")} joined through your link` +
              (credits.pending ? ` · ${credits.pending} in review` : "") +
              (credits.used ? ` · ${credits.used} used` : "")
            : "None yet: they appear after a linked client's first payment"}
        </small>
        <AddUserButton className="button button--secondary button--sm ov-link__add" onAdded={onAddedUser} />
      </div>
    </section>
  );
}
